import { useEffect, useMemo } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

import {
  useGetClassCurationsDetailApiQuery,
  useGetClassPublicCurationsDetailApiQuery,
} from "api/class/curation/hooks";
import { useProducts } from "api/market/product/hooks";
import Loading from "component/Loading";
import CourseCard from "component/klass/CourseCard";
import { Product } from "types/market";

import { CLASS_ROUTE } from "@/constant/urlConstant";
import { tokenState } from "state";
import PageWrapper from "@/component/PageWrapper";

const KlassCuration: React.FC = () => {
  const navigate = useNavigate();
  const { curationId } = useParams<{ curationId: string }>();
  const token = useRecoilValue(tokenState);
  const isLogin = useMemo(() => !!token && token && !!token.user_type, [token]);

  const {
    isFetching: isGetClassPublicCurationsDetailApiFetching,
    data: getClassPublicCurationsDetailApiData,
  } = useGetClassPublicCurationsDetailApiQuery(curationId);
  const { isFetching: isGetClassCurationsDetailApiFetching, data: getClassCurationsDetailApiData } =
    useGetClassCurationsDetailApiQuery(isLogin, curationId);

  const curation = useMemo(
    () =>
      isLogin
        ? getClassCurationsDetailApiData?.curation
        : getClassPublicCurationsDetailApiData?.curation,
    [isLogin, getClassPublicCurationsDetailApiData, getClassCurationsDetailApiData],
  );
  const courses = useMemo(() => curation?.courses, [curation]);

  const productIds = useMemo(
    () => courses?.map((course) => course.products[0]?.productId) || [],
    [courses],
  );
  const { data: products } = useProducts(
    { ids: productIds },
    {
      select: (response: { hits: Product[] }) => {
        return response?.hits || [];
      },
      enabled: !!token?.uid && (productIds || [])?.length > 0,
    },
  ) as { data: Product[]; isLoading: boolean };

  // MARK: 큐레이션 ID가 없으면 클래스 홈으로 이동합니다.
  useEffect(() => {
    if (!curationId) navigate(CLASS_ROUTE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isProductSoldOut = (productId: string) => {
    return products?.find((product) => product.id === productId)?.is_sold_out;
  };

  if (isGetClassCurationsDetailApiFetching || isGetClassPublicCurationsDetailApiFetching)
    return <Loading small />;

  if (!curation || !courses) return <Navigate to={`/${CLASS_ROUTE}`} replace />;

  return (
    <PageWrapper className="px-0 !pt-0">
      <section className="max-w-[1200px] mx-auto">
        <div className="lg:mx-auto lg:mt-6 lg:max-w-[1200px] lg:border-b-2 lg:border-pureblack lg:px-0 lg:py-[10px] lg:text-left border-b-8 border-solid  border-secondary-100 px-4 py-[14px] text-center flex items-end justify-between">
          <h2 className="break-keep text-[20px] font-bold leading-[27.6px] text-secondary-800 lg:text-[30px] lg:leading-[36px]">
            {curation.title}
          </h2>
        </div>
      </section>
      <section className="max-w-[1200px] mt-[24px] mx-auto px-5 xl:px-0">
        <div className="flex items-center gap-[8px] text-[18px] font-semibold mb-[20px]">
          <h3 className="text-secondary-900">전체</h3>
          <span className="text-primary-700">{courses.length}</span>
        </div>
        <div className="w-full grid grid-cols-2 gap-x-[12px] gap-y-[24px] lg:grid-cols-4 lg:gap-x-[4px] lg:gap-y-[40px]">
          {courses.map((course) => (
            <Link
              key={course.courseId}
              className="block"
              to={`/${CLASS_ROUTE}/course/${course.courseId}`}
            >
              <CourseCard
                thumbnail={course.thumbnailImage.link}
                lecturers={course.lecturerNames}
                title={course.title}
                regularPrice={course.products[0]?.regularPrice}
                salePrice={course.products[0]?.salePrice}
                topic={course.groupedBadges.topics}
                topicCategory={course.groupedBadges.topicCategory}
                level={course.groupedBadges.level?.join(",")}
                isFree={course.isFree}
                isSoldOut={isProductSoldOut(course.products[0]?.productId || "")}
              />
            </Link>
          ))}
        </div>
      </section>
    </PageWrapper>
  );
};

export default KlassCuration;
